import React, { useMemo } from 'react';
import { Box, TextButton } from '@wix/design-system';

interface IconConfig {
  element: JSX.Element;
  baseClassName?: string;
  moreClassName?: string;
  lessClassName?: string;
}

interface CollapseButtonProps {
  totalCount: number;
  visibleCount: number;
  initialCount: number;
  showMoreText: string;
  showLessText: string;
  onToggle: (shouldShowMore: boolean) => void;
  iconConfig?: IconConfig;
}

const CollapseButton: React.FC<CollapseButtonProps> = React.memo(
  ({
    totalCount,
    visibleCount,
    initialCount,
    showMoreText,
    showLessText,
    onToggle,
    iconConfig,
  }) => {
    const isShowingAll = visibleCount >= totalCount;
    const hasMoreToShow = totalCount > visibleCount;

    const buttonConfig = useMemo(() => {
      if (totalCount <= initialCount) {
        return null;
      }

      if (isShowingAll) {
        return {
          text: showLessText,
          onClick: () => onToggle(false),
          dataHook: 'show-less-button',
          shouldShowMore: false,
        };
      }

      if (hasMoreToShow) {
        return {
          text: showMoreText,
          onClick: () => onToggle(true),
          dataHook: 'show-more-button',
          shouldShowMore: true,
        };
      }

      return null;
    }, [
      totalCount,
      initialCount,
      isShowingAll,
      hasMoreToShow,
      showMoreText,
      showLessText,
      onToggle,
    ]);

    const renderedIcon = useMemo(() => {
      if (!iconConfig || !buttonConfig) return undefined;

      const { element, baseClassName, moreClassName, lessClassName } =
        iconConfig;

      const iconClasses = [
        baseClassName,
        buttonConfig.shouldShowMore ? moreClassName : lessClassName,
      ]
        .filter(Boolean)
        .join(' ');

      return iconClasses ? (
        <Box className={iconClasses}>{element}</Box>
      ) : (
        element
      );
    }, [iconConfig, buttonConfig?.shouldShowMore]);

    if (!buttonConfig) {
      return null;
    }

    return (
      <Box align="right" marginTop="18px">
        <TextButton
          skin="dark"
          underline="always"
          weight="normal"
          onClick={buttonConfig.onClick}
          dataHook={buttonConfig.dataHook}
          suffixIcon={renderedIcon}
        >
          {buttonConfig.text}
        </TextButton>
      </Box>
    );
  },
);

CollapseButton.displayName = 'CollapseButton';

export default CollapseButton;
